'use client';

import { useEffect, useState } from 'react';
import type { ShellConfig } from '../portal/shellTypes';
import {
  announcePreviewReady,
  applyPreviewThemeMode,
  previewAllowedOrigins,
  readPreviewConfigUpdate,
  type PreviewThemeMode,
} from './previewProtocol';

export interface ShellPreviewState {
  /** The shell the admin is editing; null until its first message. */
  config: ShellConfig | null;
  locale: string;
  themeMode: PreviewThemeMode;
}

/**
 * The client half of a host's {@code /studio-preview/shell} route: tells the
 * admin the preview is ready and returns the shell config, locale and theme
 * mode it sends, updated on every message. The host renders the shell from
 * {@code config} with its own providers once it is non-null.
 *
 * @param allowedOrigins origins allowed to drive the preview; omitted, the
 *   {@code NEXT_PUBLIC_PREVIEW_ALLOWED_ORIGINS} the host was built with.
 */
export function useShellPreviewConfig(
  allowedOrigins?: string | readonly string[],
  initialLocale = 'en',
): ShellPreviewState {
  const listed = allowedOrigins ?? process.env.NEXT_PUBLIC_PREVIEW_ALLOWED_ORIGINS;
  const key = typeof listed === 'string' ? listed : (listed ?? []).join(',');
  const [state, setState] = useState<ShellPreviewState>({
    config: null,
    locale: initialLocale,
    themeMode: 'light',
  });

  useEffect(() => {
    const origins = previewAllowedOrigins(key, window.location.origin);
    const onMessage = (event: MessageEvent) => {
      const update = readPreviewConfigUpdate(event, origins);
      if (!update) return;
      if (update.themeMode) applyPreviewThemeMode(update.themeMode);
      setState((current) => ({
        config: update.config ?? current.config,
        locale: update.locale ?? current.locale,
        themeMode: update.themeMode ?? current.themeMode,
      }));
    };
    window.addEventListener('message', onMessage);
    announcePreviewReady();
    return () => window.removeEventListener('message', onMessage);
  }, [key]);

  return state;
}
